import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, CalendarDays, Calculator, Clock, Database, Download, FileText, Package, Trash2, Users, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { dashboardAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import IngestionDashboard from '../IngestionDashboard';
import { ActionQueue } from './ActionQueue';
import { QuotationStatusBar, QuotationTrendChart } from './DashboardCharts';
import { HeroCommercialBand } from './HeroCommercialBand';
import { InquiryFunnelStrip } from './InquiryFunnelStrip';
import { PerformanceTiles } from './PerformanceTiles';
import { CycleTimePanel } from './CycleTimePanel';

const RANGES = [
  ['24h', 'Last 24 hours'], ['7d', 'Last 7 days'], ['14d', 'Last 14 days'], ['30d', 'Last 30 days'],
  ['60d', 'Last 60 days'], ['90d', 'Last 90 days'], ['180d', 'Last 180 days'], ['1y', 'Last year'],
];

const SECTION_LABEL = 'text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400';

const Counter = ({ label, value, icon: Icon, to, testId }) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      className="flex items-center gap-3 rounded-xl border border-slate-200/70 bg-white px-4 py-3 text-left transition-colors duration-150 hover:border-blue-200 hover:bg-blue-50/30 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
      data-testid={testId}
    >
      <Icon className="h-4 w-4 shrink-0 text-slate-400" strokeWidth={1.75} />
      <span className="flex-1 truncate text-xs text-slate-500">{label}</span>
      <span className="font-mono text-sm font-bold tabular-nums text-slate-900">{value ?? 0}</span>
    </button>
  );
};

const AdminQuickAction = ({ to, icon: Icon, label }) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      className="flex items-center gap-2.5 rounded-xl border border-slate-200/70 bg-white px-4 py-3 text-left text-sm text-slate-700 transition-colors duration-150 hover:border-blue-200 hover:bg-blue-50/30 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
      data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </span>
      {label}
    </button>
  );
};

export default function AdminDashboard({ metrics, timeRange, setTimeRange, onRefresh }) {
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const factoryReset = async () => {
    setResetting(true);
    try {
      await dashboardAPI.factoryReset();
      toast.success('Factory reset completed! All transaction data has been cleared.');
      setResetOpen(false);
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Factory reset failed');
    } finally {
      setResetting(false);
    }
  };

  const pendingApprovals = (metrics?.action_queue || [])
    .filter((item) => ['costings_draft', 'quotations_pending_approval'].includes(item.key))
    .reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="-m-4 space-y-6 bg-[#F5F7FA] p-4 lg:-m-6 lg:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 lg:text-4xl">Admin Dashboard</h1>
          <p className="mt-1 text-sm text-slate-500">Inquiry to order — live commercial picture</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger
              className="h-11 w-[190px] rounded-xl border-slate-200 bg-white pl-3 text-sm shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
              data-testid="time-range-select"
            >
              <CalendarDays className="mr-2 h-4 w-4 text-blue-600" strokeWidth={1.75} />
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              {RANGES.map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline" size="icon"
            className="h-11 w-11 rounded-xl border-slate-200 bg-white text-slate-400 hover:border-rose-200 hover:text-rose-600"
            title="Factory reset" onClick={() => setResetOpen(true)} data-testid="factory-reset-btn"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <HeroCommercialBand metrics={metrics} />
      <InquiryFunnelStrip funnel={metrics?.inquiry_funnel} />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="space-y-5 xl:col-span-7">
          <ActionQueue items={metrics?.action_queue} />
          <QuotationTrendChart data={metrics?.monthly_trends || []} />
        </div>
        <div className="space-y-5 xl:col-span-5">
          <PerformanceTiles performance={metrics?.performance} />
          <QuotationStatusBar data={metrics?.quotation_status_breakdown || []} />
        </div>
      </div>

      <CycleTimePanel timeRange={timeRange} />

      <div className="space-y-5 border-t border-slate-200/80 pt-7">
        <p className={SECTION_LABEL}>Master data &amp; system</p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="master-data-counters">
          <Counter label="Active users" value={metrics?.active_users} icon={Users} to="/users" testId="counter-active-users" />
          <Counter label="Parts" value={metrics?.total_parts} icon={Package} to="/parts" testId="counter-total-parts" />
          <Counter label="Costed parts" value={metrics?.costed_parts} icon={Calculator} to="/costings" testId="counter-costed-parts" />
          <Counter label="Process flows" value={metrics?.process_flows} icon={FileText} to="/process-flows" testId="counter-process-flows" />
          <Counter label="Pending users" value={metrics?.pending_users} icon={Clock} to="/users?status=pending" testId="counter-pending-users" />
        </div>

        <IngestionDashboard />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="space-y-3">
            <p className={SECTION_LABEL}>Quick actions</p>
            <div className="grid grid-cols-2 gap-3">
              <AdminQuickAction to="/users" icon={Users} label="Manage Users" />
              <AdminQuickAction to="/audit-logs" icon={Activity} label="Audit Logs" />
              <AdminQuickAction to="/machines" icon={Package} label="Machine Master" />
              <AdminQuickAction to="/exports" icon={Download} label="Data Exports" />
            </div>
          </div>
          <div className="space-y-3">
            <p className={SECTION_LABEL}>System health</p>
            <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
              {[['API', 'Healthy'], ['Database', 'Connected'], ['PDF engine', 'Available']].map(([label, state]) => (
                <div key={label} className="flex items-center justify-between px-4 py-3">
                  <span className="flex items-center gap-2 text-sm text-slate-600">
                    <Database className="h-3.5 w-3.5 text-slate-400" strokeWidth={1.75} />{label}
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-600">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{state}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-slate-600">Pending approvals</span>
                <span
                  className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700"
                  data-testid="system-pending-approvals"
                >
                  {pendingApprovals} items
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200/80 pt-5 text-xs text-slate-400">
        <span className="font-heading font-bold uppercase tracking-[0.14em] text-slate-500">
          Sturdfix <span className="font-sans font-normal normal-case tracking-normal text-slate-400">| Precision Fastening Solutions</span>
        </span>
        <span className="tracking-[0.14em]">DATA · INSIGHTS · GROWTH</span>
      </footer>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" /> Factory Reset — Confirm Action
            </DialogTitle>
            <DialogDescription>
              This permanently deletes ALL transaction data: parts, process flows, costings, quotations,
              customers, machines and audit logs. User accounts are preserved. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={factoryReset} disabled={resetting} data-testid="confirm-factory-reset">
              {resetting ? 'Resetting...' : 'Yes, Delete All Data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
